import { useState, useRef, useEffect } from 'react';
import useSWR from 'swr';
import { toast } from 'react-hot-toast';
import { PaperPlaneRightIcon } from '@phosphor-icons/react';
import { createId } from '@paralleldrive/cuid2';

import { Button } from '../button/Button';
import { TextArea } from '../textarea/TextArea';
import { Avatar } from '../avatar/Avatar';
import { formatDateTime } from '../../utils/date';
import { getDisplayError } from '../../utils/get-display-error';
import classNames from '../../utils/classnames';
import { useTranslation } from '../../contexts/translation-context';
import type { TranslationKey } from '../../translation/types';
import { SpinnerBlock } from '../Spinner';
import { useWebsocket } from '../../contexts/websocket-context';
import type {
  OrderChatMessagesResponse,
  OrderChatSendResponse,
} from '../../app/order/endpoints/chat/OrderChatEndpoint.types';

export interface OrderChatProps {
  orderId: string;
  fetchMessages: (orderId: string, cursor?: string, take?: number) => Promise<OrderChatMessagesResponse>;
  sendMessage: (orderId: string, content: string) => Promise<OrderChatSendResponse>;
  currentUserId: string;
}

function getSideBadge(
  side: 'carrier' | 'internal',
  i18n: (key: TranslationKey) => string,
): { text: string; bgColor: string } {
  if (side === 'internal') {
    return { text: i18n('chat.internal'), bgColor: 'bg-info' };
  }
  return { text: i18n('chat.carrier'), bgColor: 'bg-brand' };
}

export const OrderChat: React.FC<OrderChatProps> = (props) => {
  const { orderId, fetchMessages, sendMessage, currentUserId } = props;
  const { i18n } = useTranslation();
  const { websocket: wsClient } = useWebsocket();
  const [messageContent, setMessageContent] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const { data, error, mutate } = useSWR(['order-chat', orderId], () => fetchMessages(orderId));

  // Subscribe to real-time chat updates via WebSocket
  useEffect(() => {
    wsClient.send({
      ref: createId(),
      method: 'chat/subscribe',
      data: { orderId },
    });

    const disposable = wsClient.onMessage((msg) => {
      if (msg.method === 'chat/new-message' && msg.data.orderId === orderId) {
        mutate();
      }
    });

    return () => disposable.dispose();
  }, [orderId]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [data?.messages]);

  const doSend = async () => {
    const trimmedContent = messageContent.trim();
    if (!trimmedContent || isSending) return;

    setIsSending(true);
    try {
      await sendMessage(orderId, trimmedContent);
      setMessageContent('');
      await mutate();
      toast.success(i18n('chat.messageSent'));
    } catch (err) {
      toast.error(i18n('chat.messageError').replace('{{error}}', getDisplayError(err)));
    } finally {
      setIsSending(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    await doSend();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      doSend();
    }
  };

  if (error) {
    return <div className="flex items-center justify-center p-8 text-negative-text">{getDisplayError(error)}</div>;
  }

  if (!data) {
    return <SpinnerBlock message={i18n('chat.title')} />;
  }

  // API returns newest first, reverse for chronological display
  const messages = [...data.messages].reverse();

  return (
    <div className="flex flex-col" style={{ height: '500px' }}>
      {/* Messages area - scrollable */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-surface-sunken">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-fg-muted">
            <p>{i18n('chat.noMessages')}</p>
          </div>
        ) : (
          messages.map((message) => {
            const isCurrentUser = message.senderId === currentUserId;
            const sideBadge = getSideBadge(message.senderSide, i18n);

            return (
              <div
                key={message.id}
                className={classNames('flex gap-3', {
                  'flex-row-reverse': isCurrentUser,
                })}
              >
                {/* Avatar */}
                <div className="shrink-0">
                  <Avatar name={message.senderName} />
                </div>

                {/* Message content */}
                <div
                  className={classNames('flex flex-col gap-1 max-w-[70%]', {
                    'items-end': isCurrentUser,
                    'items-start': !isCurrentUser,
                  })}
                >
                  {/* Sender name and role badge */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-fg">{message.senderName}</span>
                    <span
                      className={classNames('text-xs px-2 py-0.5 rounded text-white font-medium', sideBadge.bgColor)}
                    >
                      {sideBadge.text}
                    </span>
                  </div>

                  {/* Message bubble */}
                  <div
                    className={classNames('rounded-lg px-4 py-2 wrap-break-word', {
                      'bg-brand text-white': isCurrentUser,
                      'bg-white border border-border text-fg': !isCurrentUser,
                    })}
                  >
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  </div>

                  {/* Timestamp */}
                  <span className="text-xs text-fg-muted">{formatDateTime(message.createdAt)}</span>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area - fixed at bottom */}
      <div className="border-t border-border p-4 bg-white">
        <form onSubmit={handleSendMessage} className="flex gap-2">
          <div className="flex-1">
            <TextArea
              value={messageContent}
              onChange={setMessageContent}
              placeholder={i18n('chat.placeholder')}
              rows={2}
              isDisabled={isSending}
              spellCheck={true}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div className="flex items-end">
            <Button
              type="submit"
              color="primary"
              isDisabled={!messageContent.trim() || isSending}
              isLoading={isSending}
              iconLeft={<PaperPlaneRightIcon className="button-icon" weight="fill" />}
            >
              {i18n('chat.sendMessage')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
